import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp, ROLES } from '../../context/AppContext';
import { ROLE_LABELS } from '../../data/mockData';
import { fetchUsers, createUser, updateUser, deleteUser, saveUserPermissions } from '../../api';
import { Button } from '../../components/ui/button';
import {
  ChevronRight,
  UserPlus,
  FileSpreadsheet,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

import UserListingView from './UserListingView';
import UserCreateWizardView from './UserCreateWizardView';
import TreeHierarchyView from './TreeHierarchyView';
import UserPermissionsView from './UserPermissionsView';
import UserDossierView from './UserDossierView';
import {
  SuccessCredentialsDialog,
  EditDossierDialog,
  ChangeRoleDialog,
} from './UserManagementDialogs';

// Dynamic Permissions Matrix Defaults
const defaultPermissions = {
  admin: { create: true, edit: true, delete: true, view: true, export: true, assign: true, approve: true, reject: true },
  supreme_manager: { create: true, edit: true, delete: false, view: true, export: true, assign: true, approve: true, reject: true },
  manager: { create: true, edit: true, delete: false, view: true, export: false, assign: true, approve: false, reject: false },
  quality_checker: { create: false, edit: false, delete: false, view: true, export: false, assign: false, approve: true, reject: true },
  field_executive: { create: false, edit: false, delete: false, view: true, export: false, assign: false, approve: false, reject: false },
  vendor: { create: true, edit: false, delete: false, view: true, export: false, assign: true, approve: false, reject: false },
  vendor_fe: { create: false, edit: false, delete: false, view: true, export: false, assign: false, approve: false, reject: false },
  client: { create: false, edit: false, delete: false, view: true, export: false, assign: false, approve: false, reject: false },
  business_partner: { create: false, edit: false, delete: false, view: true, export: false, assign: false, approve: false, reject: false },
  universal_qc: { create: false, edit: false, delete: false, view: true, export: false, assign: false, approve: true, reject: true },
  data_entry_operator: { create: false, edit: false, delete: false, view: true, export: false, assign: false, approve: false, reject: false },
};

// Mock Login Activity logs
const initialLoginLogs = [
  { id: 1, user: 'Priya Sharma', role: 'supreme_manager', timestamp: '2026-05-20 11:15:32', ip: '192.168.1.15', device: 'Chrome on Windows 11', status: 'Success' },
  { id: 2, user: 'Amit Patel', role: 'manager', timestamp: '2026-05-20 10:48:12', ip: '192.168.1.42', device: 'Safari on iPhone 15', status: 'Success' },
  { id: 3, user: 'Vikram Singh', role: 'field_executive', timestamp: '2026-05-20 09:30:05', ip: '106.220.45.102', device: 'Android App v3.2.1', status: 'Success' },
  { id: 4, user: 'Sneha Reddy', role: 'quality_checker', timestamp: '2026-05-20 09:12:44', ip: '192.168.1.88', device: 'Firefox on macOS', status: 'Success' },
  { id: 5, user: 'Unknown User', role: 'none', timestamp: '2026-05-20 08:05:12', ip: '203.0.113.5', device: 'Unknown Browser', status: 'Failed Password' },
  { id: 6, user: 'Karan Mehta', role: 'vendor', timestamp: '2026-05-19 17:40:22', ip: '182.74.55.10', device: 'Chrome on Linux', status: 'Success' },
];

export default function UserManagement() {
  const { currentRole, currentUserId } = useApp();
  const { submenu = 'all' } = useParams();
  const navigate = useNavigate();

  // Reactive DB State
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch live users directly from MySQL backend
  useEffect(() => {
    let isMounted = true;
    async function loadUsers() {
      try {
        setIsLoading(true);
        const res = await fetchUsers();
        if (isMounted && Array.isArray(res)) {
          const mapped = res.map((u, i) => ({
            ...u,
            parentName: u.parentName || (u.role === 'admin' ? 'System' : 'Rajesh Kumar'),
            parentId: u.parent_id || (u.role === 'admin' ? null : 'USR001'),
            dob: u.dob || '1992-06-15',
            doj: u.joinDate || '2026-10-24',
            address: u.address || 'Sector 5, Salt Lake, Kolkata, India',
            emergencyPhone: u.emergencyPhone || '+91 99887 76655',
            employeeId: u.employeeId || `EMP-2026-0${100 + i}`,
            username: u.username || (u.email ? u.email.split('@')[0] : u.name.toLowerCase().replace(/\s+/g, '.')),
            status: u.status || 'active',
            region: u.region || (i % 3 === 0 ? 'North' : (i % 3 === 1 ? 'West' : 'South')),
          }));
          setUsers(mapped);
        }
      } catch (err) {
        console.error("Failed to load users from MySQL:", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }
    loadUsers();
    return () => { isMounted = false; };
  }, []);

  const [permissionsMatrix, setPermissionsMatrix] = useState(defaultPermissions);
  const [loginLogs, setLoginLogs] = useState(initialLoginLogs);
  const [sessionTimeout, setSessionTimeout] = useState(30); // 30 minutes
  const [passwordPolicy, setPasswordPolicy] = useState({
    minLength: 8,
    requireCapital: true,
    requireNumber: true,
    requireSymbol: true,
  });

  // Local UI State
  const [viewMode, setViewMode] = useState('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [managerFilter, setManagerFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');

  // Creation Wizard States
  const [creationStep, setCreationStep] = useState(1);
  const [selectedCreationRole, setSelectedCreationRole] = useState('');
  const [formValues, setFormValues] = useState({});
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [idProofFile, setIdProofFile] = useState(null);
  const [agreementFile, setAgreementFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);

  // Profile Dossier Canvas States
  const [activeDossierUser, setActiveDossierUser] = useState(null);
  const [activeDossierTab, setActiveDossierTab] = useState('basic');

  // Edit / Modals States
  const [editingUser, setEditingUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [changeRoleUser, setChangeRoleUser] = useState(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [newSelectedRole, setNewSelectedRole] = useState('');

  // Auto Parent Resolver
  const currentUserObj = useMemo(() => {
    return users.find(u => u.id === currentUserId) || { id: 'USR001', name: 'Rajesh Kumar', role: 'admin' };
  }, [users, currentUserId]);

  // Determine allowed roles to create based on LOGGED-IN user hierarchy scope
  const allowedRolesToCreate = useMemo(() => {
    if (currentRole === ROLES.ADMIN || currentRole === 'admin') {
      return [
        'supreme_manager',
        'manager',
        'business_partner',
        'quality_checker',
        'field_executive',
        'vendor',
        'vendor_fe',
        'universal_qc',
        'data_entry_operator',
        'client',
      ];
    } else if (currentRole === ROLES.SUPREME_MANAGER || currentRole === 'supreme_manager') {
      return ['quality_checker', 'manager', 'field_executive', 'vendor', 'vendor_fe'];
    } else if (currentRole === ROLES.MANAGER || currentRole === 'manager') {
      return ['field_executive', 'vendor', 'vendor_fe'];
    } else if (currentRole === ROLES.VENDOR || currentRole === 'vendor') {
      return ['vendor_fe'];
    }
    return [];
  }, [currentRole]);

  // Determine if the current role can create any user at all
  const canCreateUsers = allowedRolesToCreate.length > 0;

  // Handle User creation step transition
  const handleStartCreation = (role) => {
    setSelectedCreationRole(role);
    setFormValues({
      fullName: '',
      contactNumber: '',
      email: '',
      address: '',
      emergencyPhone: '',
      doj: format(new Date(), 'yyyy-MM-dd'),
      dob: '1995-01-01',
      status: 'active',
      assignedRegion: 'North',
      assignedManager: 'Amit Patel',
      assignedArea: '',
      aadhaarNumber: '',
      panNumber: '',
      bankDetailsAcc: '',
      bankDetailsIfsc: '',
      shiftTiming: '09:00 AM - 06:00 PM',
      qcLevel: 'L1',
      assignedDepartment: 'KYC Operations',
      companyName: '',
      authorizedPersonName: '',
      authorizedPersonNumber: '',
      companyPermanentNumber: '',
      authorizedPersonEmail: '',
      companyOfficialEmail: '',
      gstn: '',
      pincode: '',
      state: 'Maharashtra',
      city: 'Mumbai',
      clientType: 'Banking',
      contactPerson: '',
      assignedProducts: 'Address Verification',
      commissionPercentage: '10',
      vendorMapping: 'Karan Mehta',
      assignedCity: 'Mumbai',
      teamLimit: '15',
    });
    setProfilePhoto(null);
    setIdProofFile(null);
    setAgreementFile(null);
    setLogoFile(null);
    setCreationStep(2);
  };

  // Submit and Auto-generate Employee
  const handleSaveCreatedUser = async (e) => {
    e.preventDefault();
    if (!formValues.fullName && !formValues.companyName) {
      toast.error('Please fill in required fields');
      return;
    }

    const nameForCreds = formValues.fullName || formValues.companyName || 'User';
    const splitName = nameForCreds.split(' ');
    const first = splitName[0].toLowerCase();
    const last = splitName[1] ? splitName[1].toLowerCase() : 'user';
    const username = `${first}.${last}`;
    const empId = `EMP-2026-${Math.floor(1000 + Math.random() * 9000)}`;
    const tempPassword = `Temp@2026!${Math.floor(100 + Math.random() * 900)}`;
    const generatedId = `USR${Math.floor(100 + Math.random() * 900)}`;
    const emailToSave = formValues.email || formValues.companyOfficialEmail || `${username}@adventures.com`;
    const phoneToSave = formValues.contactNumber || formValues.authorizedPersonNumber || '+91 99999 88888';

    try {
      // 1. Post to MySQL Backend API
      await createUser({
        id: generatedId,
        name: nameForCreds,
        email: emailToSave,
        role: selectedCreationRole,
        status: formValues.status || 'active',
        phone: phoneToSave,
        parent_id: currentUserObj?.id || 'USR001',
        password: tempPassword,
        joinDate: formValues.doj || format(new Date(), 'yyyy-MM-dd'),
        avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100'
      });

      const newUser = {
        id: generatedId,
        name: nameForCreds,
        email: emailToSave,
        role: selectedCreationRole,
        status: formValues.status || 'active',
        phone: phoneToSave,
        joinDate: formValues.doj || format(new Date(), 'yyyy-MM-dd'),
        avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100',
        parentName: currentUserObj.name,
        parentId: currentUserObj.id,
        dob: formValues.dob,
        doj: formValues.doj,
        address: formValues.address || formValues.companyAddress || 'Kolkata, India',
        emergencyPhone: formValues.emergencyPhone || '+91 99999 00000',
        employeeId: empId,
        username: username,
        region: formValues.assignedRegion || 'North',
      };

      setUsers(prev => [newUser, ...prev]);

      // Track simulated logs
      setLoginLogs(prev => [
        {
          id: prev.length + 1,
          user: currentUserObj.name,
          role: currentUserObj.role,
          timestamp: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
          ip: '192.168.1.1',
          device: 'Web App Console',
          status: `Created User ${empId}`,
        },
        ...prev,
      ]);

      setGeneratedCredentials({
        empId,
        username,
        tempPassword,
        email: newUser.email,
        phone: newUser.phone,
        roleName: ROLE_LABELS[selectedCreationRole] || selectedCreationRole,
      });

      toast.success(`User ${nameForCreds} successfully saved to MySQL database! Credentials generated.`);
    } catch (error) {
      console.error("Failed to create user in MySQL:", error);
      const errMsg = error.response?.data?.message || 'Failed to save user in database.';
      toast.error(errMsg);
    }
  };

  // Reset Credentials / Dispatch
  const finishCredentialsView = () => {
    setGeneratedCredentials(null);
    setCreationStep(1);
    navigate('/users/all');
  };

  // Toggle User Status
  const handleToggleStatus = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    const nextStatus = user.status === 'active' ? 'inactive' : 'active';
    try {
      await updateUser({ id: userId, status: nextStatus });
      setUsers(prev =>
        prev.map(u => {
          if (u.id === userId) {
            return { ...u, status: nextStatus };
          }
          return u;
        })
      );
      toast.success(`User ${user.name} is now ${nextStatus} in database.`);
    } catch (err) {
      console.error("Failed to update status in DB:", err);
      toast.error("Failed to update status in database.");
    }
  };

  // Trigger Password Reset
  const handleResetPassword = async (user) => {
    const nextPassword = `Reset@2026#${Math.floor(100 + Math.random() * 900)}`;
    try {
      await updateUser({ id: user.id, password: nextPassword });
      toast.success(`Password reset successful for ${user.name}! Temporary code: ${nextPassword}`, {
        duration: 5000,
      });
    } catch (err) {
      console.error("Failed to reset password:", err);
      toast.error("Failed to reset password in database.");
    }
  };

  // Trigger Role Change
  const triggerChangeRole = (user) => {
    setChangeRoleUser(user);
    setNewSelectedRole(user.role);
    setShowRoleDialog(true);
  };

  const handleSaveRoleChange = async () => {
    if (!changeRoleUser) return;
    try {
      await updateUser({ id: changeRoleUser.id, role: newSelectedRole });
      setUsers(prev =>
        prev.map(u => {
          if (u.id === changeRoleUser.id) {
            return { ...u, role: newSelectedRole };
          }
          return u;
        })
      );
      toast.success(`Role for ${changeRoleUser.name} updated to ${ROLE_LABELS[newSelectedRole]} in database`);
      setShowRoleDialog(false);
      setChangeRoleUser(null);
    } catch (err) {
      console.error("Failed to update role in DB:", err);
      toast.error("Failed to update role in database.");
    }
  };

  // Save edits
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      await updateUser({
        id: editingUser.id,
        name: editingUser.name,
        email: editingUser.email,
        phone: editingUser.phone,
        role: editingUser.role,
        status: editingUser.status,
      });
      setUsers(prev =>
        prev.map(u => {
          if (u.id === editingUser.id) {
            return editingUser;
          }
          return u;
        })
      );
      toast.success(`User dossier updated in database for ${editingUser.name}`);
      setShowEditDialog(false);
      setEditingUser(null);
    } catch (err) {
      console.error("Failed to update user in DB:", err);
      toast.error("Failed to update user in database.");
    }
  };

  // Filtered lists based on submenus and selected filter states
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      // 1. Submenu Route filters
      if (submenu === 'active' && u.status !== 'active') return false;
      if (submenu === 'inactive' && u.status !== 'inactive') return false;

      // 2. Local filters
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (regionFilter !== 'all' && u.region !== regionFilter) return false;
      if (managerFilter !== 'all' && u.parentName !== managerFilter) return false;
      if (vendorFilter !== 'all' && u.vendorMapping !== vendorFilter) return false;

      // 3. Search text
      if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        const matchesName = u.name.toLowerCase().includes(query);
        const matchesEmail = u.email.toLowerCase().includes(query);
        const matchesEmp = u.employeeId.toLowerCase().includes(query);
        const matchesPhone = u.phone.includes(query);
        return matchesName || matchesEmail || matchesEmp || matchesPhone;
      }

      return true;
    });
  }, [users, submenu, searchQuery, roleFilter, regionFilter, managerFilter, vendorFilter]);

  // Unique managers and vendors for filter selectors
  const filterOptions = useMemo(() => {
    const managers = Array.from(new Set(users.map(u => u.parentName).filter(Boolean)));
    const regions = Array.from(new Set(users.map(u => u.region).filter(Boolean)));
    return { managers, regions };
  }, [users]);

  // Hierarchical visual tree builder from users
  // Build a tree matching: Admin -> Supreme Manager -> Manager -> QC/FE/Vendor -> Vendor FE
  const orgTreeData = useMemo(() => {
    // Find admins as root nodes
    const admins = users.filter(u => u.role === 'admin');
    
    const buildNode = (user) => {
      // Find downline children (where parentId === user.id)
      const children = users.filter(u => u.parentId === user.id).map(buildNode);
      return {
        id: user.id,
        name: user.name,
        role: ROLE_LABELS[user.role] || user.role,
        roleKey: user.role,
        avatar: user.avatar,
        children: children,
        userRef: user,
      };
    };

    return admins.map(buildNode);
  }, [users]);

  // Hierarchy Node select handler
  const handleSelectTreeNode = (node) => {
    if (node.userRef) {
      setActiveDossierUser(node.userRef);
      setActiveDossierTab('basic');
    }
  };

  // Toggle checkmarks inside the permission toggler grid
  const handleTogglePermission = (roleKey, permType) => {
    setPermissionsMatrix(prev => {
      const nextRolePerms = { ...prev[roleKey] };
      nextRolePerms[permType] = !nextRolePerms[permType];
      toast.success(`Permission updated: [${ROLE_LABELS[roleKey]}] - ${permType.toUpperCase()} is now ${nextRolePerms[permType] ? 'ENABLED' : 'DISABLED'}`);
      return { ...prev, [roleKey]: nextRolePerms };
    });
  };

  // Export User Matrix
  const handleExportUsers = () => {
    toast.info('Exporting spreadsheet of user hierarchies... Done! (Simulated download)');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-management-page">
      {/* Header Panel */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
            <span>User Control Module</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-blue-600 font-bold capitalize">{submenu.replace('-', ' ')}</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
            Hierarchy-Based User Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage dynamic credentials, validation rules, corporate tree dossiers, and dynamic permissions.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="border-slate-200 text-slate-700 hover:bg-slate-50 gap-2"
            onClick={handleExportUsers}
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            Export Hierarchy
          </Button>

          {canCreateUsers && (
            <Button
              className="bg-blue-600 hover:bg-blue-700 gap-2 text-white shadow-sm"
              onClick={() => {
                navigate('/users/create');
                setCreationStep(1);
              }}
            >
              <UserPlus className="w-4 h-4" />
              Create New User
            </Button>
          )}
        </div>
      </div>

      {/* --- ALL / ACTIVE / INACTIVE LISTING DASHBOARD --- */}
      {(submenu === 'all' || submenu === 'active' || submenu === 'inactive') && (
        <UserListingView
          submenu={submenu}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          roleFilter={roleFilter}
          setRoleFilter={setRoleFilter}
          regionFilter={regionFilter}
          setRegionFilter={setRegionFilter}
          managerFilter={managerFilter}
          setManagerFilter={setManagerFilter}
          vendorFilter={vendorFilter}
          setVendorFilter={setVendorFilter}
          viewMode={viewMode}
          setViewMode={setViewMode}
          filteredUsers={filteredUsers}
          filterOptions={filterOptions}
          setActiveDossierUser={setActiveDossierUser}
          setActiveDossierTab={setActiveDossierTab}
          setEditingUser={setEditingUser}
          setShowEditDialog={setShowEditDialog}
          triggerChangeRole={triggerChangeRole}
          handleResetPassword={handleResetPassword}
          handleToggleStatus={handleToggleStatus}
        />
      )}

      {/* --- CREATE USER WIZARD --- */}
      {submenu === 'create' && (
        <UserCreateWizardView
          canCreateUsers={canCreateUsers}
          creationStep={creationStep}
          setCreationStep={setCreationStep}
          selectedCreationRole={selectedCreationRole}
          allowedRolesToCreate={allowedRolesToCreate}
          handleStartCreation={handleStartCreation}
          handleSaveCreatedUser={handleSaveCreatedUser}
          formValues={formValues}
          setFormValues={setFormValues}
          currentUserObj={currentUserObj}
          currentRole={currentRole}
          users={users}
          navigate={navigate}
        />
      )}

      {/* --- TREE HIERARCHY MAP --- */}
      {submenu === 'hierarchy' && (
        <TreeHierarchyView
          orgTreeData={orgTreeData}
          handleSelectTreeNode={handleSelectTreeNode}
        />
      )}

      {/* --- DYNAMIC ROLE PERMISSIONS CRUD MATRIX & ACCOUNT POLICIES --- */}
      {submenu === 'permissions' && (
        <UserPermissionsView
          permissionsMatrix={permissionsMatrix}
          handleTogglePermission={handleTogglePermission}
          sessionTimeout={sessionTimeout}
          setSessionTimeout={setSessionTimeout}
          passwordPolicy={passwordPolicy}
          setPasswordPolicy={setPasswordPolicy}
          loginLogs={loginLogs}
        />
      )}

      {/* --- DYNAMIC PROFILE TABBED DOSSIER CANVASES --- */}
      <UserDossierView
        activeDossierUser={activeDossierUser}
        setActiveDossierUser={setActiveDossierUser}
        activeDossierTab={activeDossierTab}
        setActiveDossierTab={setActiveDossierTab}
        users={users}
        permissionsMatrix={permissionsMatrix}
        handleTogglePermission={handleTogglePermission}
      />

      {/* --- SUCCESS MODAL CREATED CREDENTIALS --- */}
      <SuccessCredentialsDialog
        generatedCredentials={generatedCredentials}
        finishCredentialsView={finishCredentialsView}
      />

      {/* --- EDIT DOSSIER DIALOG --- */}
      <EditDossierDialog
        showEditDialog={showEditDialog}
        setShowEditDialog={setShowEditDialog}
        editingUser={editingUser}
        setEditingUser={setEditingUser}
        handleSaveEdit={handleSaveEdit}
      />

      {/* --- CHANGE ROLE DIALOG --- */}
      <ChangeRoleDialog
        showRoleDialog={showRoleDialog}
        setShowRoleDialog={setShowRoleDialog}
        changeRoleUser={changeRoleUser}
        newSelectedRole={newSelectedRole}
        setNewSelectedRole={setNewSelectedRole}
        handleSaveRoleChange={handleSaveRoleChange}
      />
    </div>
  );
}
